const _ = require('lodash')
const {createRefToCommon} = require('../commonSchemaUtils')
const {
    isCompoundCompatibleSchemaValueType,
    compoundSlotRefTypesFor,
    cssVarNamePattern
} = require('./compoundCompatibilityBuilder')

/**
 * @param {string[]} propertyPaths
 * @returns {string}
 */
const buildVariableConnectionsPattern = propertyPaths => `^(?:${propertyPaths.join('|')})$`

/**
 * @param {Record<string, Array<{path: string, schemaValueType?: string}>>} singleNodeVariableConnections
 * @returns {Record<string, string[]>}
 */
const groupCompoundCompatiblePaths = singleNodeVariableConnections => {
    const grouped = {}
    Object.values(singleNodeVariableConnections)
        .flat()
        .forEach(ref => {
            if (!ref.schemaValueType || !isCompoundCompatibleSchemaValueType(ref.schemaValueType)) return
            ;(grouped[ref.schemaValueType] = grouped[ref.schemaValueType] ?? []).push(ref.path)
        })
    return _.mapValues(grouped, _.uniq)
}

/**
 * @param {string[]} refTypes
 * @param {{items: Array<{pattern: string, refTypes: string[], compoundConnection?: string}>}} [variableTypesByPattern]
 * @returns {*}
 */
const buildVariableConnectionReference = (refTypes, variableTypesByPattern) => ({
    shouldCollect: false,
    shouldValidate: true,
    referencedMap: 'variables',
    $ref: createRefToCommon('reference'),
    refTypes,
    ...(variableTypesByPattern ? {variableTypesByPattern} : {})
})

/**
 * @param {string[]} propertyPaths
 * @param {string} variableName
 * @returns {*}
 */
const buildVariableConnectionsItem = (propertyPaths, variableName) => {
    const pattern = buildVariableConnectionsPattern(propertyPaths)
    return {
        type: 'object',
        properties: {
            path: {
                type: 'string',
                pattern
            },
            variableId: buildVariableConnectionReference([variableName], {
                items: [{pattern, refTypes: [variableName]}]
            }),
            cssVarName: false
        },
        required: ['path', 'variableId']
    }
}

/**
 * @param {string} schemaValueType
 * @param {string[]} propertyPaths
 * @returns {*[]}
 */
const buildCompoundBranch = (schemaValueType, propertyPaths) => {
    if (!propertyPaths.length) return []
    const refTypes = compoundSlotRefTypesFor(schemaValueType)
    if (!refTypes.length) return []
    const pattern = buildVariableConnectionsPattern(propertyPaths)
    return [
        {
            type: 'object',
            properties: {
                path: {type: 'string', pattern},
                variableId: buildVariableConnectionReference(refTypes, {
                    items: [{pattern, refTypes, compoundConnection: 'cssVarName'}]
                }),
                cssVarName: {type: 'string', pattern: cssVarNamePattern}
            },
            required: ['path', 'variableId', 'cssVarName']
        }
    ]
}

/**
 * @param {Record<string, Array<{path: string, schemaValueType?: string}>>} singleNodeVariableConnections
 * @returns {{type: string, properties: {variableConnections: {type: string, items: {anyOf: *, unevaluatedProperties: boolean}}}}}
 */
const buildVariableConnectionsForSchema = singleNodeVariableConnections => {
    const wholeValueBranches = Object.entries(singleNodeVariableConnections).map(([variableName, variableReferences]) =>
        buildVariableConnectionsItem(
            variableReferences.map(ref => ref.path),
            variableName
        )
    )
    const compoundBranches = Object.entries(groupCompoundCompatiblePaths(singleNodeVariableConnections)).flatMap(
        ([svt, paths]) => buildCompoundBranch(svt, paths)
    )
    const itemBranches = [...compoundBranches, ...wholeValueBranches]
    return {
        type: 'object',
        properties: {
            variableConnections: {
                type: 'array',
                items: {anyOf: itemBranches, unevaluatedProperties: false}
            }
        }
    }
}
/**
 * @param {*} variableConnectionsMap
 * @param {string} dataNodeType
 * @param {string} namespace
 * @returns {*}
 */
const buildVariableConnections = (variableConnectionsMap, dataNodeType, namespace) => {
    const dataNodeVariableConnections = variableConnectionsMap?.[namespace]?.[dataNodeType]
    if (!dataNodeVariableConnections) return

    return buildVariableConnectionsForSchema(dataNodeVariableConnections)
}

module.exports = {
    buildVariableConnections,
    buildVariableConnectionsForSchema
}
